import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useAuth } from '../components/AuthContext';
import { useInstitution } from '../components/InstitutionContext';
import OutlinedButton from '../components/OutlinedButton';
import FilledButton from '../components/FilledButton';
import IconTextChip from '../components/IconTextChip';
import TextOnlyChip from '../components/TextOnlyChip';
import SiteFooter from '../components/SiteFooter';
import {
  submitFeedback,
  fetchMyFeedbacks,
  fetchInstitutions,
} from '../services/api';

const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function FeedbackPage() {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const [institutionName, setInstitutionName] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [issue, setIssue] = useState('');
  const [page, setPage] = useState('');
  const [details, setDetails] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const nameFromUser = user?.userprofile?.institution?.display_name;
    if (nameFromUser) {
      setInstitutionName(nameFromUser);
      return;
    }
    if (!selectedInstitution) {
      setInstitutionName('');
      return;
    }
    const loadInst = async () => {
      try {
        const list = await fetchInstitutions();
        const inst = list.find((i) => String(i.id) === String(selectedInstitution));
        if (inst) setInstitutionName(inst.display_name);
      } catch (err) {
        console.error('Failed to load institution:', err);
        setErrorMessage('Failed to load institution.');
      }
    };
    loadInst();
  }, [user, selectedInstitution]);

  useEffect(() => {
    if (drawerOpen) {
      fetchMyFeedbacks()
        .then((res) => setFeedbacks(res || []))
        .catch((err) => {
          console.error('Failed to load feedbacks', err);
          setErrorMessage('Failed to load feedbacks.');
        });
    }
  }, [drawerOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!issue || !details) return;
    try {
      await submitFeedback({ issue, page, details });
      // Refresh feedback list so the drawer shows the new issue
      fetchMyFeedbacks()
        .then((res) => setFeedbacks(res || []))
        .catch((err) => {
          console.error('Failed to load feedbacks', err);
          setErrorMessage('Failed to load feedbacks.');
        });
      setSuccessMessage('Feedback submitted.');
      // Reset form
      setIssue('');
      setPage('');
      setDetails('');
      // Open the drawer to display submitted feedback
      setDrawerOpen(true);
    } catch (err) {
      console.error('Failed to submit feedback', err);
      setErrorMessage('Failed to submit feedback.');
    }
  };

  return (
    <Box sx={{ maxWidth: 800, width: '100%', mx: 'auto', pt: 6, pb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, px: 2 }}>
          <Typography variant="h4" gutterBottom>
            Submit Feedback
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Let us know about issues or suggestions for improving the site.
          </Typography>
          {institutionName && (
            <Typography variant="body1" sx={{ fontStyle: 'italic', mt: 2 }}>
              {institutionName}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minWidth: 200,
          }}
        >
          <OutlinedButton
            text="View My Feedbacks"
            color="info"
            size="large"
            onClick={() => setDrawerOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          />
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Issue <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            required
            fullWidth
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
          />
        </div>
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Page
          </Typography>
          <TextField
            fullWidth
            value={page}
            onChange={(e) => setPage(e.target.value)}
          />
        </div>
        <div>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            Details <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            required
            multiline
            rows={4}
            fullWidth
            value={details}
            onChange={(e) => setDetails(e.target.value)}
          />
        </div>
        <FilledButton
          type="submit"
          text="Submit"
          color="success"
          disabled={!issue || !details}
        />
      </Box>

      <Drawer
        variant="persistent"
        anchor="right"
        open={drawerOpen}
        sx={{
          width: 320,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 320,
            top: 'calc(var(--template-frame-height, 0px) + 64px)',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">My Feedbacks</Typography>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          <List dense>
            {feedbacks.map((fb, idx) => (
              <React.Fragment key={fb.id}>
                <ListItem
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    pb: 1,
                    pt: 1,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                  onClick={() => setSelected(fb)}
                >
                  <Typography
                    sx={{
                      fontWeight: 'bold',
                      width: '100%',
                      px: 1,
                    }}
                  >
                    {fb.issue}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, px: 2, pt: 1 }}>
                    <IconTextChip
                      icon={<CalendarTodayOutlinedIcon fontSize="small" />}
                      label={formatDate(fb.created_at)}
                      color="info"
                    />
                    <TextOnlyChip label={fb.status} />
                  </Box>
                </ListItem>
                {idx < feedbacks.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Drawer>


      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage('')}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth>
        <DialogTitle>
          {selected?.issue}
          <IconButton
            onClick={() => setSelected(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Submitted {formatDate(selected.created_at)}
              </Typography>
              <Typography variant="subtitle2">Details</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selected.details}
              </Typography>
              {selected.response_content && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Response
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selected.response_content}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <OutlinedButton onClick={() => setSelected(null)} text="Close" color="info" />
        </DialogActions>
      </Dialog>
      <SiteFooter />
    </Box>
  );
}
